import { useCallback, useState } from 'react'

import type { CustomInvoiceClientDto } from 'service-api/src/types/api'

import { fetchVisitConflictingInvoices } from './conflict_check'
import { useBookingContext } from '@providers/booking'
import { captureException } from 'service-api/src/shared'
import { CustomError } from 'components-ui/src'
import { buildErrorMessage } from 'components-ui/src/shared/error'
import { useUserStatus } from '../../hooks/use_userstatus'

export const useBookingConflictModal = (locationName: string) => {
  const ctx = useBookingContext()
  const userStatus = useUserStatus()
  const [conflictInvoices, setConflictInvoices] = useState<CustomInvoiceClientDto[]>([])
  const [locationPetProfileIds, setLocationPetProfileIds] = useState<string[]>([])

  const close = useCallback(() => {
    setConflictInvoices([])
  }, [])

  const checkVisitConflicts = useCallback(
    async (args: { period: string[]; locationPetProfileIds: string[] }): Promise<boolean> => {
      const { period, locationPetProfileIds } = args
      try {
        ctx.dispatch({
          type: 'SET_ASYNC_ACTION',
          payload: { value: true },
        })
        const results = userStatus.isLoggedIn
          ? await fetchVisitConflictingInvoices(locationName, period, locationPetProfileIds)
          : []
        if (results.length > 0) {
          setLocationPetProfileIds(locationPetProfileIds)
          setConflictInvoices(results)
          return false
        }
      } catch (error) {
        // If conflict validation fails, do not block the user from continuing.
        captureException(new CustomError(buildErrorMessage(error), 'Conflict booking'))
        return true
      } finally {
        ctx.dispatch({
          type: 'SET_ASYNC_ACTION',
          payload: { value: false },
        })
      }
      return true
    },
    [ctx, locationName]
  )

  return {
    conflictInvoices,
    locationPetProfileIds,
    isOpen: conflictInvoices.length > 0,
    close,
    checkVisitConflicts,
  }
}
